import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, MapPin, Check, Plus, Car, Users,
  Sparkles, ChevronRight, ChevronDown, Share2, Activity,
  Calendar, Clock, Music, Camera, UtensilsCrossed,
  MessageSquare, User, Mail, MessageCircle, Copy,
  Shirt, Gift, ListChecks, Edit, X, ExternalLink,
  Video, Trophy, Briefcase, Hotel, Baby, Star, Book, DollarSign,
  CheckSquare, Lightbulb, FileText,
  Heart, UserPlus, Snowflake, Box,
  HeartHandshake, HelpCircle, Target, BookOpen, Mic, Image as ImageIcon,
  UserCircle, Download, LayoutGrid
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { format, parseISO, differenceInDays, differenceInHours, differenceInMinutes, differenceInSeconds, formatDistanceToNow, isPast } from "date-fns";
import { de } from "date-fns/locale";
import { isFeatureEnabled, ORGANISATIONS_FEATURES, MITMACHEN_FEATURES, MEDIEN_FEATURES } from "@/config/featureGroupsNew";
import { API, BACKEND_URL } from "@/lib/api";
import EventRecapView from "@/components/EventRecapView";

// Feature Components
import EventPlaylist from "@/components/EventPlaylist";
import EventWishlist from "@/components/EventWishlist";
import EventTimeline from "@/components/EventTimeline";
import EventCarpool from "@/components/EventCarpool";
import EventTaskList from "@/components/EventTaskList";
import EventBudget from "@/components/EventBudget";
import EventTips from "@/components/EventTips";
import EventGuestbook from "@/components/EventGuestbook";
import EventLocation from "@/components/EventLocation";
import EventDresscode from "@/components/EventDresscode";
import EventVideoUpload from "@/components/EventVideoUpload";
import EventAudioUpload from "@/components/EventAudioUpload";
import SeatingPlan from "@/components/SeatingPlan";
import SelfieGame from "@/components/SelfieGame";
import Gamification from "@/components/Gamification";
import MatchingTool from "@/components/MatchingTool";
import TeamMissions from "@/components/TeamMissions";
import EmployeeSpotlight from "@/components/EmployeeSpotlight";
import WorkshopDocuments from "@/components/WorkshopDocuments";
import BucketList from "@/components/BucketList";
import DigitalYearbook from "@/components/DigitalYearbook";
import DishVoting from "@/components/DishVoting";
import NameAdvisor from "@/components/NameAdvisor";
import YearReview from "@/components/YearReview";
import GuestFeed from "@/components/GuestFeed";
import SecretSantaGenerator from "@/components/SecretSantaGenerator";
import MemoryBox from "@/components/MemoryBox";
import GenderGuessGame from "@/components/GenderGuessGame";
import VisionBoard from "@/components/VisionBoard";
import EventPhotoUpload from "@/components/EventPhotoUpload";
import EventGalleryNew from "@/components/EventGalleryNew";
import MediaDownloadCenter from "@/components/MediaDownloadCenter";
import GuestManagement from "@/components/GuestManagement";
import ErrorBoundary from "@/components/ErrorBoundary";
import EventChat from "@/pages/EventChat";
import GuestViewFloatingButton from "@/components/GuestViewFloatingButton";

const THEME = {
  fontSerif: "'Cormorant Garamond', serif",
  fontSans: "'Plus Jakarta Sans', sans-serif"
};

const EVENT_TYPE_LABELS = {
  // English Defaults
  wedding: { label: "Hochzeit", emoji: "💍", image: "/images/headers/wedding.png" },
  birthday: { label: "Geburtstag", emoji: "🎂", image: "/images/headers/birthday_new.png" },
  party: { label: "Party", emoji: "🎉", image: "/images/headers/party_new.png" },
  bachelorette: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  graduation: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  anniversary: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  babyshower: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  baby_shower: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  baby: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  corporate: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  christmas: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  newyear: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  new_year: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  easter: { label: "Ostern", emoji: "🐰", image: "/images/headers/easter.png" },
  valentine: { label: "Valentinstag", emoji: "💖", image: "/images/headers/valentine.png" },

  // German & Aliases
  hochzeit: { label: "Hochzeit", emoji: "💍", image: "/images/headers/wedding.png" },
  geburtstag: { label: "Geburtstag", emoji: "🎂", image: "/images/headers/birthday_new.png" },
  feier: { label: "Party", emoji: "🎉", image: "/images/headers/party_new.png" },
  jga: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  junggesellenabschied: { label: "Junggesellenabschied", emoji: "🍾", image: "/images/headers/bachelorette.png" },
  abschluss: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  abschlussfeier: { label: "Abschlussfeier", emoji: "🎓", image: "/images/headers/graduation.png" },
  jubilaeum: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  jubiläum: { label: "Jubiläum", emoji: "💝", image: "/images/headers/anniversary.png" },
  babyparty: { label: "Babyparty", emoji: "👶", image: "/images/headers/baby-shower.png" },
  taufe: { label: "Taufe", emoji: "✝️", image: "/images/headers/baby-shower.png" },
  gender_reveal: { label: "Gender Reveal", emoji: "👶", image: "/images/headers/baby-shower.png" },
  firma: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  firmenevent: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  business: { label: "Firmenevent", emoji: "🏢", image: "/images/headers/corporate.png" },
  weihnachten: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  weihnachtsfeier: { label: "Weihnachtsfeier", emoji: "🎄", image: "/images/headers/christmas.png" },
  silvester: { label: "Silvester", emoji: "🎆", image: "/images/headers/newyear.png" },
  ostern: { label: "Ostern", emoji: "🐰", image: "/images/headers/easter.png" },
  valentinstag: { label: "Valentinstag", emoji: "💖", image: "/images/headers/valentine.png" },

  other: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" },
  sonstiges: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" },
  individuell: { label: "Individuell", emoji: "✨", image: "/images/headers/party_new.png" }
};

const ICON_MAP = {
  Briefcase, Hotel, Baby, MapPin, Users, Shirt, Clock, Star, Book, DollarSign,
  ListChecks, CheckSquare, Lightbulb, Gift, FileText, Trophy,
  Camera, Heart, UtensilsCrossed, UserPlus, Calendar, Snowflake, Box,
  HeartHandshake, HelpCircle, Target, Car, BookOpen, Video, Music, Mic, Image: ImageIcon,
  UserCircle, MessageSquare, Download,
  CalendarClock: Clock
};

// Component mapping for features
const COMPONENT_MAP = {
  // Basis
  guestbook: EventGuestbook,
  location: EventLocation,
  dresscode: EventDresscode,

  // Organisation
  timeline: EventTimeline,
  schedule: EventTimeline,
  budget: EventBudget,
  tasks: EventTaskList,
  tips: EventTips,
  seating_plan: SeatingPlan,
  carpool: EventCarpool,

  // Wünsche & Erinnerungen
  wishlist: EventWishlist,
  bucket_list: BucketList,
  yearbook: DigitalYearbook,
  memory_box: MemoryBox,
  vision_board: VisionBoard,
  year_review: YearReview,
  name_advisor: NameAdvisor,

  // Spiele & Mitmachen
  selfie_game: SelfieGame,
  gamification: Gamification,
  matching_tool: MatchingTool,
  team_missions: TeamMissions,
  dish_voting: DishVoting,
  secret_santa: SecretSantaGenerator,
  gender_guess: GenderGuessGame,

  // Business
  employee_spotlight: EmployeeSpotlight,
  workshop_docs: WorkshopDocuments,

  // Medien
  music_upload: EventPlaylist,
  video_upload: EventVideoUpload,
  audio_upload: EventAudioUpload,
  photo_upload: EventGalleryNew,
  gallery: EventGalleryNew,
  chat: EventChat,
};

const safelyFormatDate = (dateString, formatStr) => {
  try {
    if (!dateString) return "";
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return "";
    return format(date, formatStr, { locale: de });
  } catch (e) {
    console.error("Date formatting error:", e);
    return "";
  }
};

const safelyFormatTimeAgo = (dateString) => {
  try {
    if (!dateString) return "";
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return "";
    return formatDistanceToNow(date, { addSuffix: true, locale: de });
  } catch (e) {
    return "";
  }
};



const GuestNav = ({ activeTab, onTabChange }) => (
  <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-xl border-t border-gray-100 py-2 px-2 md:px-6 pb-6 z-50 flex justify-around items-end shadow-[0_-10px_40px_rgba(0,0,0,0.05)]">
    <button onClick={() => onTabChange('feed')} className={`flex flex-col items-center gap-1 p-2 transition-all ${activeTab === 'feed' ? 'text-brand-accent scale-110' : 'text-gray-300 hover:text-gray-400'}`}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${activeTab === 'feed' ? 'bg-brand-accent/10' : 'bg-transparent'}`}>
        <LayoutGrid className="w-6 h-6" />
      </div>
      <span className="text-[9px] font-black uppercase tracking-wider">Feed</span>
    </button>

    <button onClick={() => onTabChange('programm')} className={`flex flex-col items-center gap-1 p-2 transition-all ${activeTab === 'programm' ? 'text-brand-accent scale-110' : 'text-gray-300 hover:text-gray-400'}`}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${activeTab === 'programm' ? 'bg-brand-accent/10' : 'bg-transparent'}`}>
        <Calendar className="w-6 h-6" />
      </div>
      <span className="text-[9px] font-black uppercase tracking-wider">Programm</span>
    </button>

    <button onClick={() => onTabChange('users')} className={`flex flex-col items-center gap-1 p-2 transition-all ${activeTab === 'users' ? 'text-brand-accent scale-110' : 'text-gray-300 hover:text-gray-400'}`}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${activeTab === 'users' ? 'bg-brand-accent/10' : 'bg-transparent'}`}>
        <Users className="w-6 h-6" />
      </div>
      <span className="text-[9px] font-black uppercase tracking-wider">Gäste</span>
    </button>

    <button onClick={() => onTabChange('profile')} className={`flex flex-col items-center gap-1 p-2 transition-all ${activeTab === 'profile' ? 'text-brand-accent scale-110' : 'text-gray-300 hover:text-gray-400'}`}>
      <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${activeTab === 'profile' ? 'bg-brand-accent/10' : 'bg-transparent'}`}>
        <User className="w-6 h-6" />
      </div>
      <span className="text-[9px] font-black uppercase tracking-wider">Profil</span>
    </button>
  </div>
);



export default function PublicEventHeroNew({ user }) {
  const { shareCode } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enabledFeatures, setEnabledFeatures] = useState({});
  const [guests, setGuests] = useState([]); // Add guests state
  const [timeline, setTimeline] = useState([]); // Add timeline state
  const [activeView, setActiveView] = useState(null);
  const [rsvpStatus, setRsvpStatus] = useState(null);
  const [guestName, setGuestName] = useState("");
  const [heroImage, setHeroImage] = useState("https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80");
  const [customTitle, setCustomTitle] = useState("");
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isRsvpLoading, setIsRsvpLoading] = useState(false);

  const [showUpsell, setShowUpsell] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [showDeclinedDialog, setShowDeclinedDialog] = useState(false);
  const [pendingRsvpStatus, setPendingRsvpStatus] = useState(null);
  const [guestEmail, setGuestEmail] = useState(""); // NEW: for non-logged in users
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Cache buster for images
  const [cacheBuster] = useState(Date.now());

  const [tempTitle, setTempTitle] = useState("");
  const [guestTab, setGuestTab] = useState('feed');
  const [selectedGuest, setSelectedGuest] = useState(null); // Add state for selected guest
  const [summary, setSummary] = useState(null);
  const [isGeneratingSummary, setIsGeneratingSummary] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Bild ist zu groß (Max 10MB)");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64String = reader.result;
      try {
        const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
        await axios.put(`${API}/events/${event.id}`,
          { cover_image: base64String },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setHeroImage(base64String);
        toast.success("Hintergrundbild aktualisiert");
      } catch (error) {
        console.error(error);
        toast.error("Fehler beim Hochladen");
      }
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateSummary = async () => {
    setIsGeneratingSummary(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const response = await axios.post(`${API}/events/${event.id}/generate-summary`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSummary(response.data.summary);
      setShowSummaryDialog(true);
      toast.success("Zusammenfassung erstellt!");
    } catch (error) {
      console.error(error);
      toast.error("Fehler beim Erstellen der Zusammenfassung");
    } finally {
      setIsGeneratingSummary(false);
    }
  };

  const isOwner = user?.id && event?.user_id === user.id;

  const handleTabChange = (tab) => {
    setGuestTab(tab);
    if (tab !== 'profile') {
      setSelectedGuest(null); // Clear selected guest when switching away or to other tabs
    } else {
      // If clicking profile tab directly, show own profile
      setSelectedGuest(null);
    }
  };

  useEffect(() => {
    if (shareCode) {
      fetchEvent();
      try {
        const savedRsvp = localStorage.getItem(`rsvp_${shareCode}`);
        if (savedRsvp) {
          const parsed = JSON.parse(savedRsvp);
          setRsvpStatus(parsed.status || (typeof parsed === 'string' ? parsed : null));
          if (parsed.name) setGuestName(parsed.name);
        }
      } catch (e) {
        console.error("Error parsing saved RSVP:", e);
        // Fallback if it was just a string
        const savedRsvp = localStorage.getItem(`rsvp_${shareCode}`);
        if (savedRsvp && !savedRsvp.startsWith('{')) {
          setRsvpStatus(savedRsvp === 'confirmed' ? 'accepted' : (savedRsvp === 'declined' ? 'declined' : null));
        }
      }
    }
  }, [shareCode]);

  useEffect(() => {
    if (user && shareCode) {
      checkUserRsvp();
    }
  }, [user, shareCode]);

  // Fetch guests and timeline when event is loaded
  useEffect(() => {
    if (event?.id) {
      fetchGuests();
      fetchTimeline();
    }
  }, [event?.id]);

  const fetchTimeline = async () => {
    try {
      const response = await axios.get(`${API}/events/${event.id}/timeline`);
      setTimeline(response.data.sort((a, b) => new Date(a.datetime) - new Date(b.datetime)));
    } catch (error) {
      // fallback for public endpoint if needed similar to guests
      console.log("Could not fetch timeline");
    }
  };

  const fetchGuests = async () => {
    try {
      const res = await axios.get(`${API}/events/${event.id}/guests`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      // Note: Public view might not have token, so endpoint needs to be public or we need a public equivalent.
      // Assuming for now the user viewing is either guest (no token) or owner.
      // If endpoint is protected, this will fail for public guests.
      // We need a public endpoint for guests list if "Gäste sehen" feature is enabled.
      // Let's try the public equivalent or assume the backend allows it for now if we use a different route?
      // Actually standard route is protected. I should check if there is a public one.
      // If not, I will use a fallback or try to hit a public route.
      // Re-reading: "public/event/:shareCode" returns event.
      setGuests(res.data);
    } catch (e) {
      // If standard fetch fails (auth), try public endpoint if exists, or just empty
      // Attempting a likely public route:
      try {
        const publicRes = await axios.get(`${API}/public/event/${shareCode}/guests`);
        setGuests(publicRes.data);
      } catch (err) {
        console.log("Could not fetch guests");
      }
    }
  };

  useEffect(() => {
    if (event?.date) {
      const interval = setInterval(() => {
        try {
          const now = new Date();
          if (!event.date) return;
          const eventDate = parseISO(event.date);
          if (isNaN(eventDate.getTime())) return;

          setCountdown({
            days: differenceInDays(eventDate, now),
            hours: differenceInHours(eventDate, now) % 24,
            minutes: differenceInMinutes(eventDate, now) % 60,
            seconds: differenceInSeconds(eventDate, now) % 60
          });
        } catch (e) {
          // Silent fail for countdown
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [event]);

  const checkUserRsvp = async () => {
    try {
      const response = await axios.get(`${API}/public/event/${shareCode}/check-rsvp?user_id=${user.id}`);
      if (response.data.found) {
        setRsvpStatus(response.data.status === 'confirmed' ? 'accepted' : 'declined');
        if (response.data.name) setGuestName(response.data.name);
      }
    } catch (error) { }
  };

  const fetchEvent = async () => {
    try {
      const response = await axios.get(`${API}/public/event/${shareCode}`);
      setEvent(response.data);
      setEnabledFeatures(response.data.enabled_features || {});

      if (response.data.cover_image) {
        let path = response.data.cover_image;
        if (path.startsWith('data:')) {
          setHeroImage(path);
        } else {
          if (path.includes('uploads/')) {
            const parts = path.split('uploads/');
            path = `/api/media/${parts[parts.length - 1]}`;
          }
          if (!path.startsWith('/') && !path.startsWith('http')) {
            path = `/${path}`;
          }
          let coverImageUrl = path.startsWith('http') ? path : `${BACKEND_URL}${path}`;

          // Add cache buster
          if (coverImageUrl.includes('/api/media/')) {
            coverImageUrl = `${coverImageUrl}?v=${cacheBuster}`;
          }

          setHeroImage(coverImageUrl);
        }
      } else {
        // Fallback to event type default image
        const typeKey = response.data.event_type?.toLowerCase() || 'other';
        const typeInfo = EVENT_TYPE_LABELS[typeKey] || EVENT_TYPE_LABELS.other;
        setHeroImage(typeInfo.image);
      }

      setCustomTitle(response.data.hero_title || response.data.title || "SAVE THE DATE");
      setTempTitle(response.data.hero_title || response.data.title || "SAVE THE DATE"); // Initialize temp title
      if (response.data.summary) {
        setSummary(response.data.summary);
      }
    } catch (error) {
      toast.error("Event nicht gefunden");
    } finally {
      setLoading(false);
    }
  };

  const handleRsvp = (status) => {
    setPendingRsvpStatus(status);
    if (!guestName && (user?.display_name || user?.name)) {
      setGuestName(user.display_name || user.name);
    }
    setShowNamePrompt(true);
  };

  const submitRsvp = async () => {
    if (!guestName.trim()) {
      toast.error("Bitte gib deinen Namen ein.");
      return;
    }

    setIsRsvpLoading(true);
    try {
      const rsvpStatusValue = pendingRsvpStatus === 'accepted' ? 'confirmed' : 'declined';

      const headers = {};
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const payload = {
        name: guestName.trim(),
        email: user?.email || guestEmail.trim() || "",
        plus_one: 0,
        notes: ""
      };

      await axios.post(`${API}/public/event/${shareCode}/rsvp?status=${rsvpStatusValue}`, payload, { headers });

      setRsvpStatus(pendingRsvpStatus);
      localStorage.setItem(`rsvp_${shareCode}`, JSON.stringify({ status: pendingRsvpStatus, name: guestName.trim() }));
      toast.success(pendingRsvpStatus === 'accepted' ? "Zusage gespeichert!" : "Abmeldung gespeichert.");

      setShowNamePrompt(false);

      if (pendingRsvpStatus === 'accepted') {
        setShowUpsell(true);
      } else if (pendingRsvpStatus === 'declined') {
        setShowDeclinedDialog(true);
      }
    } catch (error) {
      console.error("RSVP Error:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setIsRsvpLoading(false);
    }
  };

  const saveCustomTitle = async (titleToSave) => {
    try {
      // Assuming endpoint supports hero_title updates
      if (isOwner && event?.id) {
        await axios.put(`${API}/events/${event.id}`, { hero_title: titleToSave }, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setCustomTitle(titleToSave);
        setIsEditingTitle(false);
        toast.success("Titel gespeichert");
      }
    } catch (e) {
      toast.error("Speichern fehlgeschlagen");
    }
  };

  if (loading) return <div className="min-h-screen bg-brand-background flex items-center justify-center"><div className="w-10 h-10 border-4 border-brand-accent border-t-transparent rounded-full animate-spin" /></div>;
  if (!event) return (
    <div className="min-h-screen bg-brand-background flex flex-col items-center justify-center p-6 text-center">
      <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
        <X className="w-8 h-8 text-gray-400" />
      </div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Event nicht gefunden</h1>
      <p className="text-gray-500 max-w-md mx-auto mb-6">
        Das gesuchte Event existiert nicht oder du hast keinen Zugriff. Bitte überprüfe den Link.
      </p>
      <Button onClick={() => navigate('/dashboard')} variant="outline">
        Zum Dashboard
      </Button>
    </div>
  );

  // Feature Detail View Rendering
  // Feature Detail Components Map
  const COMPONENT_MAP = {
    guestbook: EventGuestbook,
    location: EventLocation,
    dresscode: EventDresscode,
    timeline: EventTimeline,
    schedule: EventTimeline,
    budget: EventBudget,
    tasks: EventTaskList,
    tips: EventTips,
    seating_plan: SeatingPlan,
    carpool: EventCarpool,
    wishlist: EventWishlist,
    bucket_list: BucketList,
    yearbook: DigitalYearbook,
    memory_box: MemoryBox,
    vision_board: VisionBoard,
    year_review: YearReview,
    name_advisor: NameAdvisor,
    selfie_game: SelfieGame,
    gamification: Gamification,
    matching_tool: MatchingTool,
    team_missions: TeamMissions,
    dish_voting: DishVoting,
    face_voting: DishVoting, // Placeholder
    guest_who: GuestManagement, // Placeholder/Direct link
    secret_santa: SecretSantaGenerator,
    gender_guess: GenderGuessGame,
    employee_spotlight: EmployeeSpotlight,
    workshop_docs: WorkshopDocuments,
    photo_upload: EventPhotoUpload,
    music_upload: EventPlaylist,
    video_upload: EventVideoUpload,
    audio_upload: EventAudioUpload,
    media_download: MediaDownloadCenter,
    chat: EventChat,
  };
  if (activeView) {
    // Determine Component
    const Component = COMPONENT_MAP[activeView];
    const featureGroups = [...ORGANISATIONS_FEATURES, ...MITMACHEN_FEATURES, ...MEDIEN_FEATURES];
    const feature = featureGroups.find(f => f.key === activeView);

    return (
      <div className="min-h-screen bg-brand-background relative p-4 md:p-12">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex items-center gap-6 mb-10">
            <button onClick={() => setActiveView(null)} className="w-14 h-14 bg-white rounded-full flex items-center justify-center border border-gray-200 shadow-xl hover:bg-brand-accent hover:text-white transition-all group">
              <ArrowLeft className="w-6 h-6 text-gray-800 group-hover:text-white" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{feature?.label || activeView}</h1>
              <p className="text-xs font-bold uppercase tracking-widest text-brand-accent mt-1">{event.title}</p>
            </div>
          </div>

          <div className="bg-white rounded-[1.5rem] md:rounded-[2.5rem] p-4 md:p-10 shadow-xl border border-gray-100 min-h-[50vh]">
            {Component ? (
              <ErrorBoundary fallback={
                <div className="text-center py-20">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <X className="w-8 h-8 text-red-500" />
                  </div>
                  <p className="text-gray-900 font-bold text-lg mb-2">Oops! Etwas ist schiefgelaufen</p>
                  <p className="text-gray-500 text-sm mb-4">Diese Funktion kann momentan nicht geladen werden.</p>
                  <Button onClick={() => setActiveView(null)} variant="outline">
                    Zurück zum Feed
                  </Button>
                </div>
              }>
                <Component
                  eventId={event.id}
                  shareCode={shareCode}
                  isOwner={isOwner}
                  user={user}
                  guestName={guestName}
                />
              </ErrorBoundary>
            ) : (
              <div className="text-center py-20 text-gray-400">
                <p className="uppercase tracking-widest font-bold">Modul wird geladen...</p>
                <p className="text-sm mt-2">Funktionalität für "{feature?.label}" folgt.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Feature Categories for listing
  const visibleFeatures = [
    ...ORGANISATIONS_FEATURES.filter(f => isFeatureEnabled(f.key, enabledFeatures)),
    ...MITMACHEN_FEATURES.filter(f => isFeatureEnabled(f.key, enabledFeatures)),
    ...MEDIEN_FEATURES.filter(f => isFeatureEnabled(f.key, enabledFeatures))
  ].map(f => {
    const IconComponent = ICON_MAP[f.icon] || ICON_MAP[f.key] || Star;
    return {
      ...f,
      icon: <IconComponent className="w-6 h-6" />
    };
  });

  // Guest Navigation State (Moved to top)






  // Render Main Content Switcher
  const renderGuestContent = () => {
    switch (guestTab) {
      case 'feed': return (
        <GuestFeed
          onNavigate={handleTabChange}
          guests={guests}
          timeline={timeline}
          event={event}
          visibleFeatures={visibleFeatures}
          rsvpStatus={rsvpStatus}
          onOpenFeature={setActiveView}
          isOwner={isOwner}
          guestName={guestName}
          summary={summary}
          onGenerateSummary={handleGenerateSummary}
          isGeneratingSummary={isGeneratingSummary}
          onViewSummary={() => setShowSummaryDialog(true)}
        />
      );
      case 'programm': return <EventTimeline eventId={event.id} isOwner={false} user={user} className="pb-32" />;
      case 'users': return (
        <GuestManagement
          eventId={event.id}
          guests={guests} // Pass the fetched guests
          isOwner={false}
          user={user}
          className="pb-32"
          // Pass the clickable handler
          onGuestUpdated={(guest) => {
            setSelectedGuest(guest);
            setGuestTab('profile');
          }}
          onBack={() => setGuestTab('feed')}
        />
      );
      case 'profile':
        if (selectedGuest) {
          // VIEWING ANOTHER GUEST'S PROFILE
          return (
            <div className="bg-white rounded-[2.5rem] p-8 shadow-xl relative mt-4 min-h-[50vh] animate-in fade-in slide-in-from-right-8 duration-500">
              <button onClick={() => { setSelectedGuest(null); setGuestTab('users'); }} className="absolute top-6 left-6 p-2 rounded-full bg-gray-50 hover:bg-gray-100 transition-colors">
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="flex flex-col items-center gap-4 mt-4">
                <div className="w-32 h-32 rounded-full p-1 bg-gradient-to-tr from-brand-accent to-yellow-200 shadow-xl">
                  <div className="w-full h-full rounded-full bg-white border-4 border-white overflow-hidden flex items-center justify-center">
                    {/* Helper to show initial if no image */}
                    <div className="w-full h-full bg-brand-accent/5 flex items-center justify-center text-4xl font-black text-brand-accent/30">
                      {selectedGuest.name?.charAt(0) || '?'}
                    </div>
                  </div>
                </div>
                <div className="text-center space-y-1">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedGuest.name}</h2>
                  <div className="flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-widest text-brand-accent">
                    <Heart className="w-3 h-3 fill-brand-accent" />
                    <span>Gast bei {event.title}</span>
                  </div>
                </div>

                {/* Guest Stats / Info similar to screenshot */}
                <div className="w-full grid grid-cols-2 gap-4 mt-8">
                  <div className="bg-gray-50 rounded-2xl p-4 text-center">
                    <span className="block text-2xl font-black text-gray-900">0</span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Beiträge</span>
                  </div>
                  <div className="bg-gray-50 rounded-2xl p-4 text-center">
                    <span className="block text-2xl font-black text-gray-900">
                      {selectedGuest.status === 'confirmed' ? 'JA' : selectedGuest.status === 'declined' ? 'NEIN' : '?'}
                    </span>
                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Status</span>
                  </div>
                </div>

                <div className="w-full space-y-3 mt-4">
                  <div className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center text-yellow-600">
                        <Gift className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-gray-900 text-sm">Wunschliste</p>
                        <p className="text-xs text-gray-400">Hat 0 Wünsche reserviert</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300" />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // VIEWING OWN PROFILE (Existing Logic)
        return (
          <div className="bg-white rounded-[2rem] p-8 shadow-xl text-center space-y-6 mt-10">
            <div className="w-24 h-24 bg-gray-100 rounded-full mx-auto flex items-center justify-center text-3xl font-black text-gray-300">
              {guestName ? guestName.charAt(0) : <User className="w-10 h-10 text-gray-300" />}
            </div>
            <h2 className="text-2xl font-bold">{guestName || 'Gast'}</h2>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-xs uppercase font-black text-gray-400 mb-2">Dein Status</p>
              <span className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${rsvpStatus === 'accepted' ? 'bg-brand-accent text-white' : 'bg-gray-200 text-gray-500'}`}>
                {rsvpStatus === 'accepted' ? "Zugesagt" : rsvpStatus === 'declined' ? "Abgesagt" : "Ausstehend"}
              </span>
            </div>
            <Button variant="outline" onClick={() => { localStorage.removeItem(`rsvp_${shareCode}`); window.location.reload(); }}>Abmelden / Reset</Button>
          </div>
        );
      default: return <GuestFeed onNavigate={handleTabChange} />;
    }
  };

  return (
    <ErrorBoundary>
      <div className="min-h-screen bg-brand-background text-brand-text font-['Plus_Jakarta_Sans'] selection:bg-brand-accent/20 overflow-x-hidden pb-0 md:pb-0">
        <Toaster position="top-center" richColors />

        {/* FEATURE DETAIL VIEW OVERLAY */}
        <AnimatePresence>
          {activeView && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-[100] bg-white overflow-y-auto"
            >
              <div className="min-h-screen pb-24">
                <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-100 px-4 py-4 flex items-center gap-4">
                  <button
                    onClick={() => setActiveView(null)}
                    className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-600" />
                  </button>
                  <span className="font-bold text-lg text-gray-900">
                    {ORGANISATIONS_FEATURES.find(f => f.key === activeView)?.label ||
                      MITMACHEN_FEATURES.find(f => f.key === activeView)?.label ||
                      MEDIEN_FEATURES.find(f => f.key === activeView)?.label ||
                      "Detail"}
                  </span>
                </div>
                <div className="p-4 safe-area-bottom">
                  {/* Render the component dynamically */}
                  {(() => {
                    const Component = COMPONENT_MAP[activeView];
                    return Component ? <Component eventId={event?.id} isOwner={isOwner} user={user} shareCode={shareCode} /> : <div className="p-8 text-center text-gray-500">Feature wird geladen...</div>;
                  })()}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {isOwner && <GuestViewFloatingButton />}

        {/* HERO SECTION - Always visible on top (Compact version when scrolling maybe?) */}
        <section className="relative h-[85vh] flex flex-col justify-between px-4 py-4 md:px-6 md:py-6 overflow-hidden transition-all duration-700">
          {/* ... (Existing Hero Background Image) ... */}
          <div className="absolute inset-0 z-0">
            <img
              src={heroImage}
              className="w-full h-full object-cover"
              alt="Background"
              onError={(e) => {
                const typeKey = event?.event_type?.toLowerCase() || 'other';
                const typeInfo = EVENT_TYPE_LABELS[typeKey] || EVENT_TYPE_LABELS.other;
                const safeImage = "https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80";

                // Check if we already tried the type-specific image
                // logic: if heroImage is ALREADY the type image, then that failed too.
                if (heroImage === typeInfo.image) {
                  if (e.target.src !== safeImage) {
                    setHeroImage(safeImage);
                  }
                } else {
                  setHeroImage(typeInfo.image);
                }
              }}
            />
            <div className="absolute inset-0 bg-white/20 backdrop-blur-[1px] pointer-events-none" />
            <div className="absolute inset-0 bg-gradient-to-b from-white/30 via-transparent to-brand-background pointer-events-none" />
          </div>

          {/* Edit Background Button (Owner Only) - Placed outside of background div to ensure clickability */}
          {isOwner && (
            <>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                className="hidden"
                accept="image/*"
              />
              <button
                onClick={() => fileInputRef.current.click()}
                className="absolute top-6 right-6 z-50 px-4 py-2 bg-black/40 hover:bg-black/60 backdrop-blur-md rounded-full text-white transition-all shadow-lg flex items-center gap-2 group border border-white/20"
                title="Hintergrundbild ändern"
              >
                <ImageIcon className="w-4 h-4 group-hover:scale-110 transition-transform" />
                <span className="text-xs font-bold uppercase tracking-wider">Hintergrund ändern</span>
              </button>
            </>
          )}

          {/* TOP CONTENT: Timer & Title */}
          <div 
            className="relative z-20 w-full flex flex-col items-center pt-8 gap-4 transition-transform duration-300"
            style={{ transform: `translateY(${event?.title_position_y || 0}px)` }}
          >
            {/* Header Text */}
            <span className="text-[10px] md:text-sm font-black uppercase tracking-[0.5em] text-brand-text/80 animate-in fade-in slide-in-from-top-4 duration-1000">
              Save the Date
            </span>

            {/* Past Event Hint */}
            {event?.date && isPast(parseISO(event.date)) && (
              <div className="bg-white/20 backdrop-blur-md border-2 border-white/30 rounded-full px-8 py-3 animate-in fade-in slide-in-from-top-2 shadow-xl mb-4">
                <span className="text-sm md:text-base font-black uppercase tracking-[0.2em] text-white drop-shadow-md">Das Event ist vorbei</span>
              </div>
            )}

            {/* Countdown */}
            {!event?.date || !isPast(parseISO(event.date)) && (
              <div className="flex gap-6 md:gap-12 mb-4 mt-2 animate-in fade-in slide-in-from-top-4 duration-1000 delay-200">
                {['Tage', 'Stunden', 'Minuten', 'Sekunden'].map((label, i) => {
                  const value = i === 0 ? countdown.days : i === 1 ? countdown.hours : i === 2 ? countdown.minutes : countdown.seconds;
                  return (
                    <div key={label} className="flex flex-col items-center">
                      <span className="text-3xl md:text-7xl font-serif font-bold text-brand-text leading-none drop-shadow-sm">
                        {value < 10 ? `0${value}` : value}
                      </span>
                      <span className="text-[8px] md:text-[10px] font-black uppercase tracking-widest text-brand-text opacity-70 mt-2">{label}</span>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Title & Date */}
            <div className="relative group mt-2 max-w-4xl mx-auto text-center flex flex-col items-center gap-2">
              {isEditingTitle ? (
                <div className="flex flex-col items-center gap-4 bg-white/90 backdrop-blur p-6 rounded-[2rem] shadow-xl animate-in zoom-in-50 duration-300">
                  <input
                    type="text"
                    value={tempTitle}
                    onChange={(e) => setTempTitle(e.target.value)}
                    className="text-4xl md:text-6xl font-medium text-center bg-transparent border-b-2 border-brand-accent/50 focus:border-brand-accent outline-none text-brand-text w-full placeholder:text-gray-300"
                    style={{ fontFamily: THEME.fontSerif }}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button onClick={() => saveCustomTitle(tempTitle)} className="px-6 py-2 bg-brand-accent text-white rounded-full font-bold text-sm hover:scale-105 transition-transform">Speichern</button>
                    <button onClick={() => { setIsEditingTitle(false); setTempTitle(customTitle); }} className="px-6 py-2 bg-gray-100 text-gray-600 rounded-full font-bold text-sm hover:bg-gray-200 transition-colors">Abbrechen</button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="relative inline-block px-2">
                    <h1
                      className="text-4xl sm:text-5xl md:text-9xl font-medium tracking-tight text-brand-text drop-shadow-xl leading-[0.85] break-words"
                      style={{
                        fontFamily: THEME.fontSerif,
                        textShadow: '0 2px 20px rgba(255,255,255,0.4), 0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      {customTitle}
                    </h1>
                    {isOwner && (
                      <button
                        onClick={() => setIsEditingTitle(true)}
                        className="absolute -right-8 -top-4 p-2 bg-white/50 hover:bg-white rounded-full backdrop-blur-sm shadow-sm transition-all opacity-0 group-hover:opacity-100"
                      >
                        <Edit className="w-5 h-5 text-brand-accent" />
                      </button>
                    )}
                  </div>
                  {event?.date && (
                    <span className="text-sm md:text-lg font-bold uppercase tracking-[0.2em] text-brand-text/60 mt-4">
                      {format(parseISO(event.date), "dd.MM.yyyy", { locale: de })}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>

          {/* RSVP in Hero */}
          <div className="relative z-10 w-full flex flex-col items-center pb-12">
            <div className="flex flex-col items-center gap-3 mb-6">
              {!rsvpStatus && (!event?.date || !isPast(parseISO(event.date))) && (
                <div className="flex gap-4">
                  <button onClick={() => handleRsvp('accepted')} className="px-8 py-3 rounded-full bg-white/80 backdrop-blur-md text-brand-text font-black uppercase tracking-widest text-[10px] shadow-lg hover:bg-brand-accent hover:text-white transition-all">Zusagen</button>
                  <button onClick={() => handleRsvp('declined')} className="px-8 py-3 rounded-full bg-black/5 backdrop-blur-md text-brand-text font-black uppercase tracking-widest text-[10px] hover:bg-gray-800 hover:text-white transition-all">Absagen</button>
                </div>
              )}
              {/* Event Past - Summary Actions */}
              {/* Show if event is past. We optimistically show the button if a user is logged in (owner check on backend) or if summary exists. */}
              {/* Event Past - Summary Actions */}
              {event?.date && isPast(parseISO(event.date)) && (isOwner || summary) && (
                <div className="flex flex-col items-center animate-in zoom-in-50 duration-500 mt-4">
                  <Button
                    onClick={() => summary ? setShowSummaryDialog(true) : handleGenerateSummary()}
                    disabled={isGeneratingSummary}
                    className="bg-white/90 backdrop-blur-xl hover:bg-white text-gray-900 border-2 border-white/50 px-8 py-6 rounded-2xl shadow-2xl transition-all hover:scale-105 group"
                  >
                    <Sparkles className="w-5 h-5 mr-2 text-yellow-500 group-hover:rotate-12 transition-transform" />
                    <div className="flex flex-col items-start">
                      <span className="text-xs font-black uppercase tracking-widest">
                        {summary ? "Highlight ansehen" : "Highlights erstellen"}
                      </span>
                      <span className="text-[10px] opacity-60 font-bold">
                        {summary ? "Deine KI-Zusammenfassung" : "KI-Collage generieren"}
                      </span>
                    </div>
                  </Button>
                  {isOwner && !summary && (
                    <span className="text-[9px] font-bold text-white/80 uppercase tracking-widest mt-2 bg-black/20 px-3 py-1 rounded-full backdrop-blur-sm">
                      Nur für Creator sichtbar
                    </span>
                  )}
                </div>
              )}
              {rsvpStatus === 'accepted' && <div className="px-6 py-2 bg-green-500/10 text-green-700 rounded-full text-xs font-bold flex items-center gap-2 backdrop-blur-md border border-green-500/20"><Check className="w-4 h-4" /> Du bist dabei!</div>}
            </div>
            <div onClick={() => {
              const content = document.getElementById('guest-content');
              content?.scrollIntoView({ behavior: 'smooth' });
            }} className="flex flex-col items-center gap-1 cursor-pointer hover:translate-y-2 transition-transform opacity-70 hover:opacity-100">
              <span className="text-[8px] font-black uppercase tracking-[0.3em] text-brand-text">Details</span>
              <ChevronDown className="w-5 h-5 text-brand-text animate-bounce" />
            </div>
          </div>
        </section>

        {/* CONTENT AREA */}
        <div id="guest-content" className="relative z-20 -mt-10 bg-brand-background rounded-t-[2rem] md:rounded-t-[3rem] min-h-[100dvh] px-3 md:px-8 py-6 shadow-[0_-20px_60px_rgba(0,0,0,0.1)]">
          <div className="max-w-3xl mx-auto">
            {/* RENDER ACTIVE TAB CONTENT */}
            {renderGuestContent()}
          </div>
        </div>

        {/* FIXED BOTTOM NAV FOR GUESTS */}
        <GuestNav activeTab={guestTab} onTabChange={handleTabChange} />

        {/* Dialogs ... (Existing NamePrompt, Upsell, DeclinedDialog) */}
        <Dialog open={showNamePrompt} onOpenChange={setShowNamePrompt}>
          <DialogContent className="bg-white/90 backdrop-blur-xl border-brand-accent/20 text-brand-text sm:max-w-md rounded-[2rem]">
            {/* ... Content ... */}
            <DialogHeader>
              <DialogTitle className="text-xl font-serif text-center">Wie ist dein Name?</DialogTitle>
            </DialogHeader>
            <div className="space-y-6 py-4">
              <div className="space-y-3">
                <p className="text-center text-sm text-gray-500">Damit wir dich auf die Gästeliste setzen können.</p>
                <Input
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Dein Vor- & Nachname"
                  className="text-center text-lg h-14 rounded-xl border-brand-accent/20 focus:border-brand-accent bg-white/50"
                  autoFocus
                />
                {!user && (
                  <Input
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="Deine E-Mail (für das Dashboard)"
                    type="email"
                    className="text-center text-base h-14 rounded-xl border-brand-accent/20 focus:border-brand-accent bg-white/50"
                  />
                )}
                {user && (
                  <p className="text-center text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    Bestätigung als: {user.email}
                  </p>
                )}
              </div>
              <Button
                onClick={() => submitRsvp(pendingRsvpStatus, null, guestName)}
                disabled={isRsvpLoading}
                className="w-full bg-brand-text hover:bg-brand-accent text-white rounded-xl py-6 text-sm font-bold uppercase tracking-wider"
              >
                {isRsvpLoading ? "Speichert..." : "Bestätigen"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Summary Dialog - Now Rich Experience */}
        <Dialog open={showSummaryDialog} onOpenChange={setShowSummaryDialog}>
          <DialogContent className="max-w-[100vw] sm:max-w-2xl bg-[#fdfbf7] p-0 overflow-hidden h-[100vh] sm:h-[90vh] flex flex-col border-none">
            <div className="flex-1 overflow-y-auto custom-scrollbar relative">
              <div className="absolute top-6 right-6 z-[60]">
                <button
                  onClick={() => setShowSummaryDialog(false)}
                  className="bg-white/80 hover:bg-white text-slate-900 rounded-full p-2 backdrop-blur-md transition-all shadow-xl border border-slate-100"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <EventRecapView
                summary={summary}
                event={event}
                onDownload={() => {
                  const link = document.createElement("a");
                  link.href = summary.image;
                  link.download = `Event_Recap.jpg`;
                  link.click();
                }}
                onShare={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: `Recap`,
                      text: summary.text,
                      url: window.location.href
                    }).catch(console.error);
                  }
                }}
              />
              {isOwner && (
                <div className="absolute bottom-4 left-0 right-0 z-[60] flex justify-center pb-20 pointer-events-none">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (window.confirm("Möchtest du einen neuen KI-Rückblick generieren?")) {
                        setShowSummaryDialog(false);
                        handleGenerateSummary();
                      }
                    }}
                    className="pointer-events-auto bg-white/80 backdrop-blur-md px-4 py-2 rounded-full text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-amber-600 transition-colors shadow-lg border border-white/50"
                  >
                    Neu generieren
                  </button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        <Toaster position="top-center" />
      </div>
    </ErrorBoundary>
  );
}
